import { ErrorDeAcceso } from './errores';
import { CUENTAS_DE_MENTIRA } from './cuentasDeMentira';
import type { PuertoDeIdentidad, Sesion, Usuario } from './puerto';

/**
 * El adaptador de desarrollo: comprueba contra un archivo del repo y guarda la
 * sesión en el navegador. **No autentica nada.**
 *
 * Existe para que las pantallas se puedan escribir, probar y recorrer enteras
 * sin que exista el backend, y para que el día que exista no haya que tocar más
 * que `elPuerto.ts`.
 *
 * `esDeMentira` es `true`, y de ahí cuelga el aviso en pantalla.
 */

const CLAVE = 'ilumetria.inversionistas.sesion';

/** Ocho horas: lo bastante para una jornada, lo bastante poco para notarlo. */
const DURACION_MS = 8 * 60 * 60 * 1000;

/**
 * Un retraso que no está para simular latencia, sino para que el estado
 * «entrando…» del formulario **se ejerza de verdad** al usarlo a mano. Sin él,
 * la transición es instantánea y nadie descubre que está mal escrita hasta que
 * hay un servidor de por medio.
 */
const RETRASO_MS = 300;

interface SesionGuardada {
  usuario: Usuario;
  expiraEn: number;
}

function esperar(ms: number): Promise<void> {
  return new Promise((listo) => setTimeout(listo, ms));
}

/**
 * `localStorage` puede lanzar, no sólo devolver `null`: en una ventana privada
 * o con el almacenamiento bloqueado, **acceder** al objeto ya tira. Así que
 * toda lectura y escritura va envuelta, y el fallo se trata como «no hay
 * sesión» en vez de tumbar la pantalla.
 */
function leerGuardada(): SesionGuardada | null {
  try {
    const crudo = window.localStorage.getItem(CLAVE);
    if (!crudo) return null;
    const dato = JSON.parse(crudo) as unknown;
    if (!esSesionGuardada(dato)) return null;
    // Una sesión caducada es lo mismo que no tener sesión, y se limpia al
    // pasar: dejarla ahí hace que el siguiente arranque vuelva a decidir lo
    // mismo con los mismos bytes.
    if (dato.expiraEn <= Date.now()) {
      borrarGuardada();
      return null;
    }
    return dato;
  } catch {
    return null;
  }
}

function esSesionGuardada(dato: unknown): dato is SesionGuardada {
  if (typeof dato !== 'object' || dato === null) return false;
  const posible = dato as Record<string, unknown>;
  if (typeof posible.expiraEn !== 'number') return false;
  const usuario = posible.usuario as Record<string, unknown> | undefined;
  if (typeof usuario !== 'object' || usuario === null) return false;
  return (
    typeof usuario.id === 'string' &&
    typeof usuario.correo === 'string' &&
    (usuario.nombre === null || typeof usuario.nombre === 'string')
  );
}

function guardar(sesion: SesionGuardada): void {
  try {
    window.localStorage.setItem(CLAVE, JSON.stringify(sesion));
  } catch {
    // Sin almacenamiento la sesión vive sólo en memoria. Es peor, pero es
    // mejor que no poder entrar.
  }
}

function borrarGuardada(): void {
  try {
    window.localStorage.removeItem(CLAVE);
  } catch {
    /* ídem */
  }
}

export class AdaptadorLocal implements PuertoDeIdentidad {
  readonly esDeMentira = true;

  /** Lo último que se sabe, para responder a `usuario()` sin esperar. */
  #enMemoria: Usuario | null = leerGuardada()?.usuario ?? null;

  async iniciarSesion(correo: string, contrasena: string): Promise<Sesion> {
    await esperar(RETRASO_MS);

    const limpio = correo.trim().toLowerCase();
    const cuenta = CUENTAS_DE_MENTIRA.find(
      (c) => c.correo === limpio && c.contrasena === contrasena,
    );
    // Un solo código para «no existe» y «contraseña mala», igual que haría un
    // servidor que no quiera decir qué correos tiene dados de alta.
    if (!cuenta) throw new ErrorDeAcceso('credenciales');

    const sesion: SesionGuardada = {
      usuario: { id: cuenta.id, correo: cuenta.correo, nombre: cuenta.nombre, rol: cuenta.rol },
      expiraEn: Date.now() + DURACION_MS,
    };
    guardar(sesion);
    this.#enMemoria = sesion.usuario;
    return sesion;
  }

  async pedirCodigo(_correo: string): Promise<void> {
    // Sin servidor no hay correo que mandar. **No se simula un exito**: una
    // pantalla que dice «te mandamos un codigo» cuando no hay nada detras es
    // la misma mentira que este adaptador anuncia en rojo en la otra pantalla.
    throw new ErrorDeAcceso('sinConfigurar');
  }

  async restablecer(_correo: string, _codigo: string, _contrasena: string): Promise<void> {
    throw new ErrorDeAcceso('sinConfigurar');
  }

  async cerrarSesion(): Promise<void> {
    borrarGuardada();
    this.#enMemoria = null;
  }

  async sesionActual(): Promise<Sesion | null> {
    const guardada = leerGuardada();
    this.#enMemoria = guardada?.usuario ?? null;
    return guardada;
  }

  usuario(): Usuario | null {
    return this.#enMemoria;
  }

  async credencial(): Promise<string | null> {
    // No hay token porque no hay servidor. Devolver una cadena inventada haría
    // que el cliente de datos creyera que puede llamar.
    return null;
  }
}
